// @ts-nocheck
import React, { useState } from 'react';
import { X, Upload, AlertCircle, HelpCircle, Paperclip, Trash2, TicketCheck } from 'lucide-react';
import { tone, toneForPriority } from "components/ui/tone";
import { SelectField } from "components/ui/select-field";

const NewTicketForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    priority: 'medium',
    tags: ''
  });
  const [attachments, setAttachments] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const categories = [
    { value: '', label: 'Select a category' },
    { value: 'Authentication', label: 'Login & Authentication' },
    { value: 'Email Delivery', label: 'Email Delivery Issues' },
    { value: 'Billing', label: 'Billing & Payments' },
    { value: 'Performance', label: 'Performance Issues' },
    { value: 'Feature Request', label: 'Feature Request' },
    { value: 'Integration', label: 'Third-party Integrations' },
    { value: 'API', label: 'API Issues' },
    { value: 'Editor', label: 'Template Editor' },
    { value: 'Export', label: 'Data Export' },
    { value: 'Other', label: 'Other' }
  ];

  const priorities = [
    { value: 'low', label: 'Low', description: 'General questions, minor issues' },
    { value: 'medium', label: 'Medium', description: 'Standard issues affecting workflow' },
    { value: 'high', label: 'High', description: 'Important issues blocking work' },
    { value: 'critical', label: 'Critical', description: 'System down, urgent issues' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.trim().length < 5) {
      newErrors.title = 'Title must be at least 5 characters long';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters long';
    }

    if (!formData.category) {
      newErrors.category = 'Please select a category';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Process tags
      const tags = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      const ticketData = {
        ...formData,
        tags,
        title: formData.title.trim(),
        description: formData.description.trim(),
        attachments: attachments
      };

      await onSubmit(ticketData);
    } catch (error) {
      console.error('Error creating ticket:', error);
      setErrors({ submit: 'Failed to create ticket. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPriorityColor = (priority) =>
  // One urgency ramp for the whole app (critical → high → medium → low).
  // Five files each had their own and they disagreed on what "high" and
  // "low" look like; see toneForPriority.
  tone(toneForPriority(priority)).badge;

  // File upload handling
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const maxFileSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf', 'text/plain', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    const validFiles = [];
    const fileErrors = [];

    files.forEach(file => {
      if (file.size > maxFileSize) {
        fileErrors.push(`${file.name} is too large (max 10MB)`);
      } else if (!allowedTypes.includes(file.type)) {
        fileErrors.push(`${file.name} is not a supported file type`);
      } else {
        // Create a mock attachment object (in real app, you'd upload to server)
        const attachment = {
          id: `att-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          filename: file.name,
          size: file.size,
          type: file.type,
          file: file, // Store file object for potential upload
          uploadedAt: new Date()
        };
        validFiles.push(attachment);
      }
    });

    if (fileErrors.length > 0) {
      setErrors(prev => ({
        ...prev,
        attachments: fileErrors.join(', ')
      }));
    } else {
      setErrors(prev => ({
        ...prev,
        attachments: ''
      }));
    }

    if (validFiles.length > 0) {
      setAttachments(prev => [...prev, ...validFiles]);
    }

    // Clear the input
    e.target.value = '';
  };

  const removeAttachment = (attachmentId) => {
    setAttachments(prev => prev.filter(att => att.id !== attachmentId));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-surface dark:bg-dark-800 border border-line dark:border-white/[0.07] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-line-subtle dark:border-white/[0.06]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
              <TicketCheck className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-content dark:text-white">Create New Support Ticket</h2>
              <p className="text-xs text-content-muted dark:text-gray-500 mt-0.5">
                Describe your issue and we'll help you resolve it
              </p>
            </div>
          </div>
          <button aria-label="Close"
            onClick={onCancel}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-content-subtle dark:text-gray-500 hover:text-content-secondary dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-xs font-semibold uppercase tracking-wide text-content-muted dark:text-gray-500 mb-2">
              Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Brief description of your issue"
              className={`w-full px-3 py-2.5 bg-gray-50 dark:bg-white/[0.04] border rounded-xl text-sm text-content dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 dark:focus:border-blue-500 transition-colors ${
                errors.title ? 'border-red-300 dark:border-red-700' : 'border-line dark:border-white/[0.08]'
              }`}
            />
            {errors.title && (
              <p className="mt-1.5 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />
                {errors.title}
              </p>
            )}
          </div>

          {/* Category */}
          <div>
            <label htmlFor="category" className="block text-xs font-semibold uppercase tracking-wide text-content-muted dark:text-gray-500 mb-2">
              Category *
            </label>
            <SelectField
              id="category"
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className={`w-full px-3 py-2.5 bg-gray-50 dark:bg-white/[0.04] border rounded-xl text-sm text-content dark:text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 dark:focus:border-blue-500 transition-colors ${
                errors.category ? 'border-red-300 dark:border-red-700' : 'border-line dark:border-white/[0.08]'
              }`}
            >
              {categories.map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </SelectField>
            {errors.category && (
              <p className="mt-1.5 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />
                {errors.category}
              </p>
            )}
          </div>

          {/* Priority */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-content-muted dark:text-gray-500 mb-2">
              Priority *
            </label>
            <div className="grid grid-cols-2 gap-2.5">
              {priorities.map(priority => (
                <label
                  key={priority.value}
                  className={`relative flex items-start p-3 border rounded-xl cursor-pointer transition-all ${
                    formData.priority === priority.value
                      ? getPriorityColor(priority.value)
                      : 'border-line dark:border-white/[0.07] hover:bg-gray-50 dark:hover:bg-white/[0.03]'
                  }`}
                >
                  <input
                    type="radio"
                    name="priority"
                    value={priority.value}
                    checked={formData.priority === priority.value}
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">{priority.label}</span>
                      {formData.priority === priority.value && (
                        <div className="w-1.5 h-1.5 rounded-full bg-current"></div>
                      )}
                    </div>
                    <p className="text-xs text-content-muted dark:text-gray-500 mt-0.5">{priority.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-xs font-semibold uppercase tracking-wide text-content-muted dark:text-gray-500 mb-2">
              Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={5}
              placeholder="Please provide detailed information about your issue, including steps to reproduce if applicable..."
              className={`w-full px-3 py-2.5 bg-gray-50 dark:bg-white/[0.04] border rounded-xl text-sm text-content dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 dark:focus:border-blue-500 resize-vertical transition-colors ${
                errors.description ? 'border-red-300 dark:border-red-700' : 'border-line dark:border-white/[0.08]'
              }`}
            />
            {errors.description && (
              <p className="mt-1.5 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />
                {errors.description}
              </p>
            )}
            <p className="mt-1 text-xs text-content-subtle dark:text-gray-600">
              Be as specific as possible to help us resolve your issue quickly
            </p>
          </div>

          {/* Tags */}
          <div>
            <label htmlFor="tags" className="block text-xs font-semibold uppercase tracking-wide text-content-muted dark:text-gray-500 mb-2">
              Tags <span className="normal-case font-normal">(Optional)</span>
            </label>
            <input
              type="text"
              id="tags"
              name="tags"
              value={formData.tags}
              onChange={handleInputChange}
              placeholder="urgent, login, dashboard (comma-separated)"
              className="w-full px-3 py-2.5 bg-gray-50 dark:bg-white/[0.04] border border-line dark:border-white/[0.08] rounded-xl text-sm text-content dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 dark:focus:border-blue-500 transition-colors"
            />
          </div>

          {/* File Attachments */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-content-muted dark:text-gray-500 mb-2">
              Attachments <span className="normal-case font-normal">(Optional)</span>
            </label>
            
            {/* File Upload Area */}
            <div className="border-2 border-dashed border-line dark:border-white/[0.08] rounded-xl p-6 text-center hover:border-blue-400 dark:hover:border-blue-600/50 hover:bg-blue-50/30 dark:hover:bg-blue-900/[0.06] transition-all">
              <input
                type="file"
                id="file-upload"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.txt,.doc,.docx,.xls,.xlsx"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center gap-2"
              >
                <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-white/[0.06] flex items-center justify-center">
                  <Upload className="w-5 h-5 text-content-subtle dark:text-gray-500" />
                </div>
                <div>
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Click to upload files
                  </span>
                  <span className="text-sm text-content-subtle dark:text-gray-600 block">
                    or drag and drop
                  </span>
                </div>
                <p className="text-xs text-content-subtle dark:text-gray-600">
                  PNG, JPG, PDF, DOC, XLS up to 10MB each
                </p>
              </label>
            </div>

            {/* Attachment Errors */}
            {errors.attachments && (
              <p className="mt-2 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />
                {errors.attachments}
              </p>
            )}

            {/* Attached Files List */}
            {attachments.length > 0 && (
              <div className="mt-3 space-y-2">
                <h4 className="text-xs font-semibold text-content-muted dark:text-gray-500">
                  Attached Files ({attachments.length})
                </h4>
                {attachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-white/[0.03] rounded-xl border border-line-subtle dark:border-white/[0.06]"
                  >
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                      <Paperclip className="w-4 h-4 text-content-subtle dark:text-gray-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-content dark:text-white truncate">
                          {attachment.filename}
                        </p>
                        <p className="text-xs text-content-muted dark:text-gray-500">
                          {formatFileSize(attachment.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(attachment.id)}
                      className="w-7 h-7 flex items-center justify-center text-content-subtle dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title="Remove attachment"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-xl">
              <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {errors.submit}
              </p>
            </div>
          )}

          {/* Help Text */}
          <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 rounded-xl">
            <div className="flex items-start gap-2.5">
              <HelpCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-1">Tips for better support</h4>
                <ul className="text-xs text-blue-700 dark:text-blue-400 space-y-1">
                  <li>• Include specific error messages if any</li>
                  <li>• Mention what you were trying to do when the issue occurred</li>
                  <li>• Include browser and device information if relevant</li>
                  <li>• Attach screenshots if they help explain the issue</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-line-subtle dark:border-white/[0.06]">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-semibold border border-line dark:border-white/[0.08] rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-2 text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl shadow-sm hover:shadow-blue-500/20 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Creating...
                </>
              ) : (
                'Create Ticket'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewTicketForm;